import axios from "axios";
import { apiTimeout } from "Components/Misc/CaseDetails_System";
import { handleAxiosInterceptorsReponse, setRequestHeaders, showResponseDialog } from "./common";
import _ from "lodash";
import { OFFER_STTATUS } from "features/dashboard/utils/consstants";

/**
 * A class to manage interactions with the Datamart API.
 * Provides methods for initializing configuration, intercepting requests, and performing various API operations such as fetching offers, handling bookmarks, and retrieving user roles.
 */
class DATAMART_CONFIG_API {
  accessToken;
  email;
  idtoken;
  redX;
  configUrl;
  userId;
  constructor() {
    this.interceptRequests();
  }


  /**
   * Initializes the Datamart API configuration with the provided parameters.
   * Sets up access tokens, user details, and other required configurations.
   * 
   * @param {Object} config - The configuration object.
   * @param {string} config.accessToken - The access token for API authentication.
   * @param {string} config.email - The user's email address.
   * @param {string} config.idtoken - The ID token for API authentication.
   * @param {Object} config.redX - The Redux store or dispatcher.
   * @param {string} config.userId - The user's unique identifier.
   * @returns {Object|undefined} Returns an error message if initialization fails.
   */
  init({ accessToken, email, idtoken, redX, userId }) {
    if (accessToken && email) {
      this.accessToken = accessToken;
      this.email = email;
      this.idtoken = idtoken;
      this.redX = redX;
      this.userId = userId;
      // this.configUrl = configUrl;
    } else {
      return { msg: "Failed to initiate." };
    }
  }

  /**
   * Sets up Axios interceptors for handling API requests and responses.
   * Adds authentication headers to requests and processes responses to handle errors or trigger dialogs.
   */
  interceptRequests() {
    // Axios request interceptor
    axios.interceptors.response.use((response) => {
      // Handle the response - call common function to handle the response
      return handleAxiosInterceptorsReponse(response, this.redX.dispatch);
    }, (error) => {
      showResponseDialog(this.redX.dispatch, 500, { msg: error.code, panel: { title: 'Failed to connect server', msg: error.message } });
      return Promise.reject(error);
    });
    // AAxios request interceptor
    axios.interceptors.request.use((request) => {
      if (!request?.["skipAuth"]) {
        // Add headers only if `skipAuth` is not set
        request = setRequestHeaders(request);
      }
      // Restricting x-user-id to only datamart requests
      // Once proxy server is replaced with data mart, this can be removed and added to all requests
      if (request.url.includes(process.env.REACT_APP_DATAMART)) {
        request.headers["x-user-id"] = this.userId;
      }
      // Return the request
      return request;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  /**
   * Saves the provided cart details to the Datamart API.
   * 
   * @param {Array} cartDataList - The list of cart data to be saved.
   * @param {string} apiMethod - The HTTP method to use (e.g., "POST" or "PUT").
   * @returns {Promise<Object>} A promise that resolves to the API response.
   */
  saveCartDetails = async (cartDataList, apiMethod) => {
    // let params = `?save_to_cart=true&username=${this.email}`;
    const cart_api_details = {
      method: apiMethod,
      url: `/v2/offer`,
      data: cartDataList
    };
    const res = await this.genericDatamartAPI(cart_api_details);
    return res;
  }

  /**
   * Fetches offer data from the Datamart API based on the provided filter criteria.
   * 
   * @param {Object} dasboardParam - The filter criteria for fetching offers.
   * @returns {Promise<Object>} A promise that resolves to the API response containing the offer data.
   */
  fetchOffer = async (dasboardParam) => {
    const fetchoffer_api_details = {
      method: "post",
      url: `/v2/offer?includeBookmarks=true`,
      data: dasboardParam,
      operation:'read'
    };
    const res = await this.genericDatamartAPI(fetchoffer_api_details);
    return res;
  }


  /**
   * Updates the properties of an offer in the Datamart API.
   * 
   * @param {Object} editPropertiesParam - The properties to be updated.
   * @returns {Promise<Object>} A promise that resolves to the updated offer data or the API response.
   */
  editProperties = async (editPropertiesParam) => {
    const editProp_api_details = {
      method: "put",
      url: `/v2/offer`,
      data: editPropertiesParam
    };
    const res = await this.genericDatamartAPI(editProp_api_details);
    return res.data?.data ? res.data?.data : res.data
  }

  /**
   * Handles bookmarking or unbookmarking of an offer in the Datamart API.
   * 
   * @param {string} offer_id - The ID of the offer to be bookmarked or unbookmarked.
   * @param {string} action_type - The action to perform (e.g., "bookmark" or "unbookmark").
   * @returns {Promise<Object>} A promise that resolves to the API response containing the updated bookmark status.
   */
  handleBookmark = async (offer_id, action_type) => {
    let bookmark_api_details = {
      url: `/v2/offer`,
      method :  "post",
      data: { offer_id },
      operation:''
    };
    if(action_type === 'add'){
      // Prepre the POST request to add bookmark
      bookmark_api_details['xFactory'] = 'bookmark';
    }
    if(action_type === 'remove'){
      // Prepre the POST request to remove bookmark
      bookmark_api_details.method = 'delete';
      bookmark_api_details['xFactory'] = 'bookmark';
    }
    const res = await this.genericDatamartAPI(bookmark_api_details);
    return res.data?.data ? res.data?.data : res.data
  }

  handleDeleteOffer = async (offerData) => {
    const delete_api_details = {
      method: "put",
      url: `/v2/offer`,
      data: offerData
    };
    const res = await this.genericDatamartAPI(delete_api_details);
    return res.data?.data ? res.data?.data : res.data
  }


  /**
  * Fetches the access role of the current user from the Datamart API.
  * Sends a GET request to the specified endpoint and processes the response
  * to find the user's role based on their user ID. Displays an error dialog
  * if the request fails.
  */
  getUserAccessRole = async () => {
    try {
      const userRole_api_details = {
        method: "get",
        url: `/user`,
        data: undefined
      };
      const res = await this.genericDatamartAPI(userRole_api_details);
      let result = {}
      // Process the response and return the data
      // if (res.data?.data) { --- response do not have data.data
      if (res?.data) {
        const data = res?.data.find((item) => item.id === this.userId);
        result = data ? data : undefined;
      }
      return result;
    }
    catch (err) {
      showResponseDialog(this.redX.dispatch, err.response.status, { msg: err.code, panel: { title: 'Failed to connect server', msg: err.message } });
    }
  }

  /**
   * Fetches the count of offers for each status.
   * Generates mock data for offer status counts or simulates a delay for testing purposes.
   * 
   * @param {Object} dasboardParam - The filter criteria for fetching offer status counts.
   * @returns {Promise<Object>} A promise that resolves to an object containing mock offer status counts.
   */
  fetchOfferStatusCount = async (dasboardParam) => {
    const fetchoffer_api_details = {
      method: "post",
      url: `/v2/offer`,
      data: dasboardParam,
      operation:'report'
    };
    const res = await this.genericDatamartAPI(fetchoffer_api_details);
    return res;
  }

  /**
   * A generic function to make API calls (GET, POST, PUT, DELETE) to the Datamart service.
   * Handles errors and displays appropriate error dialogs if the request fails.
   * 
   * @param {Object} param - The API request details.
   * @param {string} param.method - The HTTP method (e.g., GET, POST, PUT, DELETE).
   * @param {string} param.url - The endpoint URL (relative to the Datamart base URL).
   * @param {Object} param.data - The request payload.
   * @returns {Promise<Object>} A promise that resolves to the API response or an error object.
   */
  async genericDatamartAPI({ method, url, data,operation=undefined,xFactory=undefined }) {
    try {
      const config = {
        method: method,
        url: `${process.env.REACT_APP_DATAMART}${url}`,
        data: data
      }

      if(operation && operation.trim().length > 0){
        config["headers"] = {operation};
      }
      if(xFactory && xFactory.trim().length > 0){
        config["headers"] = {'x-factory':xFactory};
      }
      const res = await axios(config);
      return res?.data;
    } catch (err) {
      const errMesg = err.response?.data?.errorMessage ? err.response?.data?.errorMessage : err?.message;
      showResponseDialog(this.redX.dispatch, err?.response?.status, { msg: err.code, panel: { title: 'Failed to connect server', msg: errMesg } });
      return { data: [], status: err?.response?.status };
    }
  }

}
const DATAMART_API = new DATAMART_CONFIG_API();
export default DATAMART_API;